import type { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios'
import axios from 'axios'
import { z } from 'zod'
import { toast } from 'sonner'
import { useAuthStore } from '@/application/stores/auth.store'

// Extend Axios config types so callers can set _retry and _skipErrorToast
declare module 'axios' {
  interface AxiosRequestConfig {
    _skipErrorToast?: boolean
  }
  interface InternalAxiosRequestConfig {
    _retry?: boolean
    _skipErrorToast?: boolean
  }
}

export const API_BASE_URL = import.meta.env.VITE_API_URL || '/api'

export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// ── Token helpers ─────────────────────────────────────────────────────────────

function getStoredToken(): string | null {
  try {
    const rawToken = localStorage.getItem('auth-storage')
    if (!rawToken) return null
    const parsed = JSON.parse(rawToken)
    return parsed?.state?.accessToken ?? null
  } catch {
    return null
  }
}

function getStoredRefreshToken(): string | null {
  try {
    const rawValue = localStorage.getItem('auth-storage')
    if (!rawValue) return null
    const parsed = JSON.parse(rawValue)
    return parsed?.state?.refreshToken ?? null
  } catch {
    return null
  }
}

// ── Response schema for the refresh endpoint ──────────────────────────────────

const refreshResponseSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string(),
})

// ── Refresh coordination state ────────────────────────────────────────────────

let isRefreshing = false
let pendingRequests: ((token: string) => void)[] = []

function drainPendingRequests(newToken: string): void {
  pendingRequests.forEach((resolve) => resolve(newToken))
  pendingRequests = []
}

// ── Request interceptor — inject Authorization header ─────────────────────────

apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getStoredToken()
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error: AxiosError) => {
    return Promise.reject(error)
  },
)

// ── Error message extractor ───────────────────────────────────────────────────

function extractResponseMessage(data: unknown): string | undefined {
  if (typeof data === 'object' && data !== null && 'message' in data) {
    if (typeof data.message === 'string') return data.message
    if (Array.isArray(data.message)) {
      const lines = data.message.filter((item): item is string => typeof item === 'string')
      if (lines.length > 0) return lines.join(' • ')
    }
  }
  return undefined
}

// ── Response interceptor — refresh on 401, toast on other errors ──────────────

const HTML_RESPONSE_MESSAGE =
  'The API returned an HTML page instead of JSON: the backend is probably unreachable (check VITE_API_URL or the dev proxy).'

apiClient.interceptors.response.use(
  (response) => {
    // A dev server (or CDN) that does not forward `/api` answers every request
    // with its SPA shell (HTTP 200, text/html). Callers would then receive a
    // string where they expect arrays/objects (`x.map is not a function`).
    // Reject instead, so queries end up in their error state with no data.
    const contentType = String(response.headers?.['content-type'] ?? '')
    if (contentType.includes('text/html') && !response.config._skipErrorToast) {
      toast.error(HTML_RESPONSE_MESSAGE, { id: 'api-html-response' })
      return Promise.reject(new Error(HTML_RESPONSE_MESSAGE))
    }
    return response
  },
  async (error: AxiosError) => {
    const status = error.response?.status
    const config = error.config

    if (status === 401 && config && !config._retry) {
      const refreshToken = getStoredRefreshToken()

      if (!refreshToken) {
        useAuthStore.getState().logout()
        return Promise.reject(error)
      }

      // If a refresh is already in flight, queue this request
      if (isRefreshing) {
        return new Promise((resolve) => {
          pendingRequests.push((newToken: string) => {
            config.headers.Authorization = `Bearer ${newToken}`
            resolve(apiClient(config))
          })
        })
      }

      config._retry = true
      isRefreshing = true

      try {
        const fetchResponse = await fetch(`${API_BASE_URL}/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken }),
        })

        if (!fetchResponse.ok) throw new Error('Refresh failed')

        const tokenData = refreshResponseSchema.parse(await fetchResponse.json())
        useAuthStore.getState().updateTokens(tokenData.accessToken, tokenData.refreshToken)

        config.headers.Authorization = `Bearer ${tokenData.accessToken}`
        drainPendingRequests(tokenData.accessToken)
        return apiClient(config)
      } catch {
        useAuthStore.getState().logout()
        pendingRequests = []
        return Promise.reject(error)
      } finally {
        isRefreshing = false
      }
    }

    // If a retry that followed a successful token refresh is still rejected with 401,
    // the newly-issued access token is also invalid — force a logout so the store
    // does not remain in an inconsistent authenticated state.
    if (status === 401 && config?._retry) {
      useAuthStore.getState().logout()
    }

    // Non-401 or already-retried errors
    if (status !== 401 && !config?._skipErrorToast) {
      const message = extractResponseMessage(error.response?.data) || error.message
      toast.error(message || 'An error occurred')
    }

    return Promise.reject(error)
  },
)

export function handleApiError(error: unknown): string {
  if (axios.isAxiosError(error)) {
    return extractResponseMessage(error.response?.data) || 'An error occurred'
  }
  if (error instanceof Error) {
    return error.message
  }
  return 'Unknown error'
}
